import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router'
import type { ReactNode } from 'react'

import { Icon } from '../components/Icon'
import { PageBand, PageShell } from '../components/PageShell'
import {
  fetchPaymentStatus,
  paymentStatusKey,
  type PaymentStatus,
} from '../api/paymentStatus'

/**
 * Where Transflow returns the patient after the hosted page (M10-T08, M29), set
 * as `TRANSFLOW_RETURN_URL`. The success/failure redirect carries
 * `?reference=<ours>`; the `trxref` fallback below is kept for old Paystack links.
 *
 * THIS SCREEN USED TO RENDER ONE STATE. A green tick and the headline "Payment
 * received", shown to everybody — including a patient who closed the payment
 * page without paying, or whose card was declined. Sub-copy hedged underneath
 * ("if you didn't finish paying, your hold will expire"), but the headline
 * asserted something the app had never confirmed, in the one journey where the
 * patient's belief and the system's state must not diverge: they could walk
 * away believing a hospital bed was held for them when nothing was held.
 *
 * That was M20 finding F5, and the owner ruled it a defect on 2026-08-21.
 * ADR-0045 is the fix: ask, then say only what the answer supports.
 *
 * The redirect still carries NO AUTHORITY — that part of the original note was
 * always right and has not changed. ADR-0020 keeps the webhook as the only
 * thing that secures a booking, and this screen moves nothing. It reads a
 * status keyed on the reference and picks a sentence.
 */
export function CheckoutReturn(): ReactNode {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const reference = params.get('reference') ?? params.get('trxref')

  const query = useQuery({
    queryKey: paymentStatusKey(reference ?? ''),
    queryFn: ({ signal }) => fetchPaymentStatus(reference as string, signal),
    enabled: reference != null,
    // The redirect almost always beats the webhook, so `pending` is the normal
    // first answer to a GOOD payment. Poll while it lasts rather than
    // concluding from it. POLL_CEILING stops this running forever on a
    // genuinely abandoned payment — after that the screen settles on what the
    // system actually guarantees, which is the SMS.
    refetchInterval: (q) =>
      q.state.data === 'pending' && q.state.dataUpdateCount < POLL_CEILING
        ? 3000
        : false,
    retry: 1,
  })

  // A missing reference, a 404, or an unreachable server all land here. None of
  // them is evidence about the money, so none of them may claim anything: the
  // unknown view says so plainly rather than defaulting to either good news or
  // bad. Defaulting to good news is the exact defect this screen had.
  const status: PaymentStatus | 'unknown' =
    reference == null || query.isError ? 'unknown' : (query.data ?? 'pending')

  const view = VIEWS[status]

  return (
    // The gradient reaches the viewport edges on the shell; the card itself is
    // `max-w-sm` and does not move.
    <PageShell
      measure={594}
      as="main"
      className="items-center justify-center bg-gradient-to-b from-accent-bg/30 to-bg-app font-sans text-ink-1"
    >
      <PageBand
        className="w-full px-6"
        contentClassName="flex flex-col items-center"
      >
        <div className="flex w-full max-w-sm flex-col items-center gap-4 rounded-r3 border border-line-1 bg-bg-app px-6 py-9 text-center shadow-card">
          <span
            className={`flex h-16 w-16 items-center justify-center rounded-full ring-4 ${view.tone}`}
            data-testid="status-mark"
          >
            <Icon name={view.icon} size={30} />
          </span>

          <h1
            className="font-display text-d-xs font-medium tracking-display"
            aria-live="polite"
          >
            {view.heading}
          </h1>

          <p className="max-w-sm text-sm text-ink-2">{view.body}</p>

          {view.note !== undefined && (
            <p className="max-w-sm text-[13px] text-ink-3">{view.note}</p>
          )}

          {reference != null && (
            <p className="rounded-full bg-bg-soft px-3 py-1 text-[12px] text-ink-3 ring-1 ring-inset ring-line-1">
              Payment reference:{' '}
              <span className="font-medium text-ink-2">{reference}</span>
            </p>
          )}

          <button
            type="button"
            onClick={() => navigate('/')}
            className="mt-2 rounded-r3 bg-accent px-5 py-2.5 text-sm font-semibold text-accent-on shadow-sm transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-lift focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            {view.cta}
          </button>
        </div>
      </PageBand>
    </PageShell>
  )
}

/** ~30s of polling. Long enough for a webhook on a normal day, short enough
 * that an abandoned payment is not left spinning. */
const POLL_CEILING = 10

type View = {
  icon: 'check' | 'clock' | 'x'
  tone: string
  heading: string
  body: string
  note?: string
  cta: string
}

/**
 * One row per thing we are entitled to say (ADR-0045). Read the headings
 * together: NOT ONE OF THEM ASSERTS A SECURED BOOKING, because securing is the
 * webhook's business and this screen cannot see it. `succeeded` claims the
 * money arrived and nothing more — which is why its body still promises the SMS
 * rather than the bed.
 */
const VIEWS: Record<PaymentStatus | 'unknown', View> = {
  succeeded: {
    icon: 'check',
    tone: 'animate-pop-in bg-bed-good-bg text-bed-good ring-bed-good/10',
    heading: 'Payment received',
    body: 'Your payment has gone through. You’ll get an SMS as soon as your place is secured — your final bed or room may still change on clinical grounds.',
    cta: 'Back to search',
  },
  pending: {
    icon: 'clock',
    tone: 'bg-bg-soft text-ink-2 ring-line-1',
    heading: 'We’re checking your payment',
    body: 'This usually takes a few seconds. You can leave this page — we’ll send an SMS as soon as your place is secured.',
    note: 'If you didn’t finish paying, your hold will expire and the place is released — you can start again any time.',
    cta: 'Back to search',
  },
  failed: {
    icon: 'x',
    tone: 'bg-sos-bg text-sos ring-sos/10',
    heading: 'Your payment didn’t go through',
    body: 'The payment was declined, so no money has left your account and no place is being held for you.',
    note: 'You can book again and try a different card or mobile money number.',
    cta: 'Try again',
  },
  abandoned: {
    icon: 'x',
    tone: 'bg-sos-bg text-sos ring-sos/10',
    heading: 'You didn’t finish paying',
    body: 'The payment page was closed before it completed, so no money has left your account and no place is being held for you.',
    note: 'Your hold will expire shortly. You can start again any time.',
    cta: 'Try again',
  },
  unknown: {
    icon: 'clock',
    tone: 'bg-bg-soft text-ink-2 ring-line-1',
    heading: 'We can’t confirm your payment right now',
    body: 'We couldn’t check the payment from here. If it went through, you’ll still get an SMS as soon as your place is secured — the confirmation does not depend on this page.',
    note: 'If you didn’t finish paying, your hold will expire and the place is released.',
    cta: 'Back to search',
  },
}
